import React from 'react';
import { toast } from 'sonner';
import { CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastOptions {
  duration?: number;
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  dismissible?: boolean;
}

export const useToast = () => {
  const showToast = (
    message: string, 
    type: ToastType = 'info', 
    options: ToastOptions = {}
  ) => {
    const { duration = 4000, dismissible = true } = options;

    const toastConfig = {
      duration,
      dismissible,
      className: 'font-medium',
    };

    switch (type) {
      case 'success':
        return toast.success(message, {
          ...toastConfig,
          icon: React.createElement(CheckCircle, { className: 'w-5 h-5 text-green-600' }),
          className: 'font-medium text-green-800',
        });
      
      case 'error':
        return toast.error(message, {
          ...toastConfig,
          icon: React.createElement(XCircle, { className: 'w-5 h-5 text-red-600' }),
          className: 'font-medium text-red-800',
          duration: 6000, // Erros ficam mais tempo visíveis
        });
      
      case 'warning':
        return toast.warning(message, {
          ...toastConfig,
          icon: React.createElement(AlertTriangle, { className: 'w-5 h-5 text-yellow-600' }),
          className: 'font-medium text-yellow-800',
        });
      
      case 'info':
      default:
        return toast.info(message, {
          ...toastConfig,
          icon: React.createElement(Info, { className: 'w-5 h-5 text-blue-600' }),
          className: 'font-medium text-blue-800',
        });
    }
  };

  const showSuccess = (message: string, options?: ToastOptions) => 
    showToast(message, 'success', options);

  const showError = (message: string, options?: ToastOptions) => 
    showToast(message, 'error', options);

  const showWarning = (message: string, options?: ToastOptions) => 
    showToast(message, 'warning', options);

  const showInfo = (message: string, options?: ToastOptions) => 
    showToast(message, 'info', options);

  const dismiss = (toastId?: string | number) => {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  };

  return {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    dismiss,
  };
};
